import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MetaComponent from "../common/MetaComponent";
import Header1 from "../headers/Header1";
import Footer1 from "../footer/Footer1";
import { BaseURL } from "../../APIs/APIUrls";
import KeywordSearch from "../modals/KeywordSearch";

const defaultSocialLinks = [
    {
        label: "Facebook",
        href: "#",
        iconClass: "icon-facebook",
    },
    {
        label: "LinkedIn",
        href: "#",
        iconClass: "icon-linkedin",
    },
    {
        label: "X",
        href: "#",
        iconClass: "icon-x",
    },
    {
        label: "Instagram",
        href: "#",
        iconClass: "icon-instargram",
    },
];

const humanizeSlug = (value) =>
    (value || "")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeRouteValue = (value) => String(value || "").trim().toLowerCase();

const stripHtml = (html) => {
    if (!html) return "";
    try {
        if (typeof document !== "undefined") {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            return (tmp.textContent || tmp.innerText || "").trim();
        }
    } catch (e) {
        // fallback to regex
    }
    return String(html).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
};

const toViewProfile = (rawProfile, fallbackUserId, fallbackSlug) => {
    if (!rawProfile) {
        return null;
    }

    const resolvedSlug = rawProfile?.ProfileURL || rawProfile?.profileURL || fallbackSlug;
    const resolvedUserId = String(
        rawProfile?.UserId || rawProfile?.VendorId || rawProfile?.vendorId || fallbackUserId || ""
    );

    return {
        UserId: resolvedUserId,
        FullName: rawProfile?.FullName || rawProfile?.Name,
        Email: rawProfile?.Email || "",
        Description: rawProfile?.ShortDescription,
        Company: "AskMeRealty",
        Photo: rawProfile?.Photo,
        PhotoAltText: rawProfile?.PhotoAltText,
        JobProfile: rawProfile?.JobProfile || "",
        Facebook: rawProfile?.Facebook || "",
        Twitter: rawProfile?.Twitter || "",
        Instagram: rawProfile?.Instagram || "",
        Youtube: rawProfile?.Youtube || "",
        Linkdin: rawProfile?.Linkdin || rawProfile?.LinkedIn || rawProfile?.Linkedin || "",
        Youtube: rawProfile?.Youtube || "",
        LinkDin: rawProfile?.LinkDin,
        ProfileURL: resolvedSlug || "",
    };
};

function Profile() {
    const { title = "" } = useParams();
    const [selectedProfileData, setSelectedProfileData] = useState(null);
    const [loading, setLoading] = useState(true);

    const { userId, profileSlug } = useMemo(() => {
        const [idPart = "", ...slugParts] = String(title || "").split("-");
        return {
            userId: idPart,
            profileSlug: slugParts.join("-") || idPart,
        };
    }, [title]);

    const fallbackProfile = useMemo(
        () =>
            toViewProfile(
                {
                    UserId: userId,
                    Name: humanizeSlug(profileSlug) || "Author Profile",
                    ShortDescription: "Author details are not available yet for this profile.",
                    ProfileURL: profileSlug,
                    JobProfile: "Content Strategist",
                    Company: "AskMeRealty",
                },
                userId,
                profileSlug
            ),
        [profileSlug, userId]
    );



    useEffect(() => {
        try {
            const rawSelectedProfile = localStorage.getItem("SelectedBloggerProfile");
            if (!rawSelectedProfile) {
                setSelectedProfileData(null);
                setLoading(false);
                return;
            }

            const selectedProfile = JSON.parse(rawSelectedProfile);
            const selectedProfileUrl = normalizeRouteValue(selectedProfile?.ProfileURL || selectedProfile?.profileURL);
            const selectedUserId = String(selectedProfile?.UserId || selectedProfile?.VendorId || "");
            const routeProfileUrl = normalizeRouteValue(profileSlug);

            const isProfileUrlMatched = Boolean(selectedProfileUrl) && selectedProfileUrl === routeProfileUrl;
            const isUserMatched = !selectedUserId || !userId || selectedUserId === String(userId);

            if (isProfileUrlMatched && isUserMatched) {
                setSelectedProfileData(toViewProfile(selectedProfile, userId, profileSlug));
                setLoading(false);
                return;
            }

            setSelectedProfileData(null);
            setLoading(false);
        } catch (error) {
            console.error("Error reading SelectedBloggerProfile:", error);
            setSelectedProfileData(null);
            setLoading(false);
        }
    }, [profileSlug, userId]);
    const activeProfile = selectedProfileData || fallbackProfile;
    const socialLinks = defaultSocialLinks.map((link) => {
        if (link.label === "Facebook" && activeProfile?.Facebook) {
            return { ...link, href: activeProfile.Facebook };
        }
        if (link.label === "LinkedIn" && (activeProfile?.LinkDin || activeProfile?.Linkdin)) {
            return { ...link, href: activeProfile.LinkDin || activeProfile.Linkdin };
        }
        if (link.label === "X" && activeProfile?.Twitter) {
            return { ...link, href: activeProfile.Twitter };
        }
        if (link.label === "Instagram" && activeProfile?.Instagram) {
            return { ...link, href: activeProfile.Instagram };
        }
        return link;
    });

    const metadata = {
        title: `${activeProfile?.FullName || "Author Profile"} || AskMeRealty - Real Estate`,
        description: stripHtml(activeProfile?.Description) || "AskMeRealty - Real Estate",
        keywords: `${activeProfile?.FullName},${activeProfile?.Facebook},${activeProfile?.LinkDin},${activeProfile?.Twitter},${activeProfile?.Instagram}, Blogger Profile,AskMeRealty, Real Estate`,
        url: window.location.href,
        siteName: "AskMeRealty",
        canonical: window.location.href,
        image: activeProfile.Photo ? BaseURL + activeProfile.Photo : "",

    };

    return (
        <>
            <MetaComponent meta={metadata} />
            <Header1 />

            <section className="flat-section">
                <div className="container">
                    <div className="mb-24 d-flex flex-wrap align-items-center justify-content-between gap-12">
                        <div>
                            {/* <p className="text-primary fw-6 text-uppercase mb-8">Author Profile</p> */}
                            <h2 className="mb-0">{activeProfile?.FullName || "Author Profile"}</h2>
                            {
                                activeProfile?.JobProfile && (
                                    <div className="text-primary fw-6">{activeProfile.JobProfile} at {activeProfile.Company}</div>
                                )
                            }

                            <div className="d-flex flex-wrap gap-12" style={{ marginTop: "20px" }}>
                                {socialLinks
                                    .filter((link) => {
                                        const href = String(link?.href || "").trim();
                                        return href && href !== "#";
                                    })
                                    .map((link) => (
                                        <a
                                            key={link.label}
                                            href={link.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="box-icon line w-44 round author-social-icon"
                                            aria-label={link.label}
                                        >
                                            <i className={`icon ${link.iconClass}`} />
                                        </a>
                                    ))}
                            </div>

                        </div>

                        <Link to="/" className="tf-btn btn-line size-1">
                            Back to Home
                        </Link>
                    </div>

                    <div className="row g-4 align-items-stretch">
                        <div className="col-lg-3">
                            <div className="widget-box-2 h-100">
                                {
                                    activeProfile?.Photo && (
                                        <div className="overflow-hidden round-20" style={{ aspectRatio: "2 / 4" }}>
                                            <img
                                                className="w-80 h-80"
                                                src={BaseURL + activeProfile.Photo}
                                                alt={activeProfile.PhotoAltText || activeProfile.FullName || "Author profile photo"}
                                                style={{ objectFit: "cover", objectPosition: "top center" }}
                                            />
                                        </div>
                                    )
                                }


                                {/* <div className="text-center">
                                    <div className="text-primary fw-6">{activeProfile?.JobProfile || "Content Strategist"}</div>
                                    <h4 className="mt-8 mb-10">{activeProfile?.FullName || "Author Profile"}</h4>
                                    <p className="text-variant-1 mb-0">
                                        {activeProfile?.Company || "AskMeRealty"}
                                    </p>
                                </div>

                                <div className="mt-24 d-grid gap-12">
                                    <div className="box-fieldset">
                                        <label>Route</label>
                                        <input
                                            type="text"
                                            className="form-control style-1"
                                            value={title || "author route"}
                                            readOnly
                                        />
                                    </div>
                                    <div className="box-fieldset">
                                        <label>User ID</label>
                                        <input
                                            type="text"
                                            className="form-control style-1"
                                            value={userId || "N/A"}
                                            readOnly
                                        />
                                    </div>
                                </div> */}
                            </div>
                        </div>

                        <div className="col-lg-9">
                            <div className="widget-box-2 h-100">
                                {/* <h5 className="title">Experience</h5> */}
                                {loading ? (
                                    <p className="mb-0">Loading author profile...</p>
                                ) : (
                                    <div
                                        className="mb-0 blog-description-content author-rich-content"
                                        dangerouslySetInnerHTML={{
                                            __html: activeProfile?.Description || "",
                                        }}
                                    />
                                )}

                                {/* <div className="mt-24">
                                    <h5 className="title mb-16">Profile Details</h5>
                                    <div className="grid-2 gap-20">
                                        <div className="box-fieldset">
                                            <label>Name</label>
                                            <input type="text" className="form-control style-1" value={activeProfile?.FullName || ""} readOnly />
                                        </div>
                                        <div className="box-fieldset">
                                            <label>Email</label>
                                            <input type="text" className="form-control style-1" value={activeProfile?.Email || ""} readOnly />
                                        </div>
                                        <div className="box-fieldset">
                                            <label>Company</label>
                                            <input type="text" className="form-control style-1" value={activeProfile?.Company || ""} readOnly />
                                        </div>
                                        <div className="box-fieldset">
                                            <label>Job Profile</label>
                                            <input type="text" className="form-control style-1" value={activeProfile?.JobProfile || ""} readOnly />
                                        </div>
                                    </div>
                                </div> */}

                                {/* <div className="mt-24">
                                    <h5 className="title mb-16">Social Media Profile Links</h5>
                                    <div className="d-flex flex-wrap gap-12">
                                        {socialLinks.map((link) => (
                                            <a
                                                key={link.label}
                                                href={link.href}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="box-icon line w-44 round author-social-icon"
                                                aria-label={link.label}
                                            >
                                                <i className={`icon ${link.iconClass}`} />
                                            </a>
                                        ))}
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer1 />
        </>
    );
}

export default Profile;
